import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { MilestoneDto, PhaseDto } from '@/api/generated';
import { useContacts, useMilestones, usePhases } from '@/api/hooks';
import { QueryBoundary } from '@/api/QueryBoundary';
import { AppFrame } from '@/design-system/chrome/AppFrame';
import { TimelineStrip } from '@/design-system/composites/TimelineStrip/TimelineStrip';
import { Icon } from '@/design-system/icon/Icon';
import { Avatar } from '@/design-system/primitives/Avatar/Avatar';
import { Btn } from '@/design-system/primitives/Btn/Btn';
import { Card } from '@/design-system/primitives/Card/Card';
import { Pill } from '@/design-system/primitives/Pill/Pill';
import { type PhaseId, STATUS_KIND, STATUS_LABEL } from '@/domain/phases';
import { formatDayMonth, hueForName, toNumber } from '@/lib/format';
import { MilestoneCreateDialog } from './MilestoneDialog';
import styles from './Timeline.module.css';

interface TimelineProps {
  variant?: 'vertical' | 'gantt';
}

// 2026-05-17 — the static RUWBOUW_SUBSTEPS + RUWBOUW_DEPENDENCY content was
// Phase-1 design-handoff fixture data. There is no Sub-step entity on the
// API, so this was always invented content that rendered on Ruwbouw
// regardless of the project's real state. Removed both arrays + the inline
// rendering blocks below. Re-introduce when a real Sub-step relation lands
// on the API.

// Which contact role drives a phase's avatars. Each phase shows up to 2.
const PHASE_PARTY_ROLES: Record<string, string[]> = {
  Ruw: ['Hoofdaannemer', 'Architect'],
  Ontw: ['Architect'],
  Verg: ['Vergunningen'],
  Tech: ['Elektrieker'],
};

export function Timeline({ variant = 'vertical' }: TimelineProps) {
  const phases = usePhases();
  const contacts = useContacts({ pageSize: 50 });
  const milestones = useMilestones();
  const [milestoneOpen, setMilestoneOpen] = useState(false);
  // BF-10 — per-row inline expansion is opt-in via the chev. Keeping it as a
  // Set scales without re-rendering siblings when one row toggles.
  const [expandedIds, setExpandedIds] = useState<ReadonlySet<string>>(() => new Set());
  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (variant === 'gantt') {
    return (
      <AppFrame>
        <div className={`row ${styles.header}`}>
          <div>
            <div className="eyebrow">Tijdlijn · gantt-view</div>
            <h1 className={styles.title}>Volledige planning</h1>
          </div>
          <div className={`row gap-8 ml-auto ${styles.headerActions}`}>
            <div className={`row gap-4 mono ${styles.legend}`}>
              <span className={`bg-ok ${styles.legendDot}`} /> afgerond
              <span className={`bg-warn ${styles.legendDot} ${styles.legendDotSpaced}`} /> bezig
              <span className={`bg-idle ${styles.legendDot} ${styles.legendDotSpaced}`} /> te
              starten
            </div>
            <Btn>
              <Icon name="filter" size={14} /> Filter
            </Btn>
            <Link to="/tijdlijn" className={styles.btnLink}>
              Mijlpalen
            </Link>
          </div>
        </div>
        <Card className={styles.ganttCard}>
          <TimelineStrip variant="gantt" />
        </Card>
      </AppFrame>
    );
  }

  return (
    <AppFrame>
      <div className={`row ${styles.header}`}>
        <div>
          <div className="eyebrow">Tijdlijn · 10 fases · 32 maanden</div>
          <h1 className={styles.title}>Volledige planning</h1>
          <p className={styles.subtitle}>
            Klik een fase open voor sub-stappen en afhankelijkheden.
          </p>
        </div>
        <div className={`row gap-8 ml-auto ${styles.headerActions}`}>
          <Btn>
            <Icon name="filter" size={14} /> Verantwoordelijken
          </Btn>
          <Link to="/tijdlijn/gantt" className={styles.btnLink}>
            <Icon name="calendar" size={14} /> Gantt-view
          </Link>
          <Btn variant="primary" onClick={() => setMilestoneOpen(true)}>
            <Icon name="plus" size={14} /> Mijlpaal
          </Btn>
        </div>
      </div>
      <MilestoneCreateDialog open={milestoneOpen} onOpenChange={setMilestoneOpen} />

      <MilestoneStrip data={milestones.data ?? []} />

      <Card className={styles.verticalCard}>
        <QueryBoundary query={phases} loading="Fases laden…">
          {(rows) => (
            <div className={styles.verticalInner}>
              <div className={styles.verticalRail} />
              <div className={styles.verticalRailFilled} />
              {rows.map((p, i) => (
                <PhaseRow
                  key={p.id}
                  phase={p}
                  last={i === rows.length - 1}
                  parties={partiesForPhase(p.id, contacts.data?.items ?? [])}
                  expanded={expandedIds.has(p.id)}
                  onToggle={() => toggleExpanded(p.id)}
                />
              ))}
            </div>
          )}
        </QueryBoundary>
      </Card>
    </AppFrame>
  );
}

function MilestoneStrip({ data }: { data: ReadonlyArray<MilestoneDto> }) {
  if (data.length === 0) return null;
  return (
    <Card className={styles.milestoneStrip}>
      <div className="row gap-8">
        <Icon name="calendar" size={14} color="var(--ink-3)" />
        <span className="eyebrow">Mijlpalen</span>
      </div>
      <div className={`col gap-6 ${styles.milestoneList}`}>
        {data.map((m) => (
          <div key={m.id} className="row gap-10">
            <span className={`mono ${styles.milestoneDate}`}>{formatDayMonth(m.date)}</span>
            <span className={styles.milestoneTitle}>{m.title}</span>
            {m.phaseId && (
              <Link to={`/fases/${m.phaseId.toLowerCase()}`} className={styles.milestonePhase}>
                <Pill kind="info" dot={false}>
                  fase {m.phaseId}
                </Pill>
              </Link>
            )}
          </div>
        ))}
      </div>
    </Card>
  );
}

function PhaseRow({
  phase,
  last,
  parties,
  expanded,
  onToggle,
}: {
  phase: PhaseDto;
  last: boolean;
  parties: { name: string; hue: number }[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const status = phase.status.toLowerCase() as 'done' | 'doing' | 'late' | 'idle';
  const done = status === 'done';
  const doing = status === 'doing';
  // Inline sub-step expansion removed 2026-05-17 with RUWBOUW_SUBSTEPS —
  // no real per-phase sub-step data exists on the API. Every row simply
  // navigates to PhaseDetail on click.
  const hasInlineDetail = false;
  const nodeClass = [
    styles.verticalNode,
    expanded ? styles.verticalNodeExpanded : '',
    done ? styles.verticalNodeDone : '',
    doing ? styles.verticalNodeDoing : '',
  ]
    .filter(Boolean)
    .join(' ');
  const rowClass = ['row', styles.verticalRow, expanded ? styles.verticalRowExpanded : '']
    .filter(Boolean)
    .join(' ');
  const labelClass = [
    styles.verticalLabel,
    doing ? styles.verticalLabelDoing : '',
    done ? styles.verticalLabelDone : '',
  ]
    .filter(Boolean)
    .join(' ');

  // Detail route uses lowercase phase ids (PHASES domain); backend accepts
  // either case in /api/phases/:id, so this keeps URLs consistent.
  const detailPath = `/fases/${phase.id.toLowerCase()}`;

  return (
    <div
      className={`${styles.verticalItem} ${
        last ? styles.verticalItemLast : expanded ? styles.verticalItemExpanded : ''
      }`}
    >
      <div className={nodeClass}>
        {done && <Icon name="check" size={10} color="var(--paper)" strokeWidth={2.5} />}
      </div>

      <div className={rowClass}>
        {hasInlineDetail ? (
          <button
            type="button"
            className={styles.chevToggle}
            aria-label={expanded ? 'Sub-stappen verbergen' : 'Sub-stappen tonen'}
            aria-expanded={expanded}
            onClick={onToggle}
          >
            <Icon name={expanded ? 'chevD' : 'chevR'} size={14} color="var(--ink-3)" />
          </button>
        ) : (
          <Icon name="chevR" size={14} color="var(--ink-3)" />
        )}
        <Link to={detailPath} className={styles.verticalMainLink}>
          <div className={styles.verticalMain}>
            <div className="row gap-10">
              <span className={labelClass}>{phase.label}</span>
              <Pill kind={STATUS_KIND[status]}>{STATUS_LABEL[status]}</Pill>
              {doing && phase.progress !== null && phase.progress !== undefined && (
                <span className={`mono ${styles.verticalProgress}`}>
                  · {Math.round(toNumber(phase.progress) * 100)}%
                </span>
              )}
            </div>
          </div>
          <div className={`mono ${styles.verticalRange}`}>
            {formatDayMonth(phase.start)} → {formatDayMonth(phase.end)}
          </div>
        </Link>
        <div className={`row gap-4 ${styles.verticalAvatars}`}>
          {parties.map((a) => (
            <Avatar key={`${phase.id}-${a.name}`} name={a.name} size={22} hue={a.hue} />
          ))}
        </div>
      </div>

      {/* Inline sub-step expansion block removed 2026-05-17 — see
          hasInlineDetail = false above. PhaseDetail handles per-phase
          drill-in via the row's <Link to=/fases/:id>. */}
    </div>
  );
}

function partiesForPhase(
  phaseId: PhaseDto['id'],
  contacts: { name: string; role: string }[],
): { name: string; hue: number }[] {
  const roles = PHASE_PARTY_ROLES[phaseId] ?? [];
  const matched = roles
    .map((role) => contacts.find((c) => c.role === role))
    .filter((c): c is { name: string; role: string } => Boolean(c));
  return matched.map((c) => ({ name: c.name.split(' ')[0], hue: hueForName(c.name) }));
}

// Re-export so other files can keep importing the type from here if they did.
export type { PhaseId };
